import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import UserAvatar from './UserAvatar';
import './ContactHoverCard.css';

const ContactHoverCard = ({
  contact,
  contactId = '',
  isSaved = false,
  isSaving = false,
  onAddContact,
  onEditContact,
  onSendMail,
  onMouseEnter,
  onMouseLeave,
}) => {
  const navigate = useNavigate();
  const [copiedEmail, setCopiedEmail] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);

  const email = String(contact?.email || '').trim();
  const displayName =
    contact?.username || contact?.displayName || email.split('@')[0] || 'Contact';

  useEffect(() => {
    if (!isEditing) {
      setEditName(displayName);
    }
  }, [displayName, isEditing]);

  const handleCopyEmail = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (!email) return;
    try {
      await navigator.clipboard.writeText(email);
      setCopiedEmail(email);
      setTimeout(() => setCopiedEmail(''), 1500);
    } catch (error) {
      console.error('Error copying email:', error);
    }
  };

  const handleAddContact = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (isSaved || isSaving || !onAddContact) return;
    await onAddContact();
  };

  const handleEditClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (!isSaved || !contactId) return;
    setEditName(displayName);
    setIsEditing(true);
  };

  const handleCancelEdit = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsEditing(false);
    setEditName(displayName);
  };

  const handleSaveEdit = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (!onEditContact || !contactId || isUpdating) return;
    setIsUpdating(true);
    try {
      await onEditContact(editName.trim());
      setIsEditing(false);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleSendMail = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (onSendMail) {
      onSendMail();
      return;
    }
    if (email) navigate(`/compose?to=${encodeURIComponent(email)}`);
  };

  return (
    <div
      className="contact-hover-card"
      role="dialog"
      aria-label={`${displayName} contact card`}
      onClick={(event) => event.stopPropagation()}
      onMouseDown={(event) => event.stopPropagation()}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <div className="contact-hover-card-top">
        <UserAvatar user={contact} size="lg" className="contact-hover-card-avatar" />
        <div className="contact-hover-card-details">
          {isEditing ? (
            <input
              type="text"
              className="contact-hover-edit-input"
              value={editName}
              onChange={(event) => setEditName(event.target.value)}
              onClick={(event) => event.stopPropagation()}
              onKeyDown={(event) => {
                if (event.key === 'Enter') handleSaveEdit(event);
                if (event.key === 'Escape') handleCancelEdit(event);
              }}
              autoFocus
              aria-label="Edit contact name"
            />
          ) : (
            <div className="contact-hover-card-name">{displayName}</div>
          )}
          <div className="contact-hover-email-row">
            <span className="contact-hover-card-email">{email}</span>
            <button
              type="button"
              className={`contact-hover-copy-btn${copiedEmail === email ? ' copied' : ''}`}
              onClick={handleCopyEmail}
              aria-label={copiedEmail === email ? 'Copied' : 'Copy email'}
              title={copiedEmail === email ? 'Copied' : 'Copy email'}
            >
              {copiedEmail === email ? (
                <svg width="17" height="17" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M9 16.17 4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                </svg>
              ) : (
                <svg width="17" height="17" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M16 1H4c-1.1 0-2 .9-2 2v12h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z" />
                </svg>
              )}
            </button>
          </div>
        </div>
        {isEditing ? (
          <div className="contact-hover-edit-actions">
            <button
              type="button"
              className="contact-hover-save-btn"
              onClick={handleSaveEdit}
              disabled={isUpdating || !editName.trim()}
              aria-label="Save contact name"
              title="Save"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M9 16.17 4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
              </svg>
            </button>
            <button
              type="button"
              className="contact-hover-cancel-btn"
              onClick={handleCancelEdit}
              disabled={isUpdating}
              aria-label="Cancel edit"
              title="Cancel"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
              </svg>
            </button>
          </div>
        ) : isSaved ? (
          <button
            type="button"
            className="contact-hover-edit-btn"
            onClick={handleEditClick}
            disabled={!contactId}
            aria-label="Edit contact"
            title="Edit contact"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" />
            </svg>
          </button>
        ) : (
          <button
            type="button"
            className="contact-hover-add-btn"
            onClick={handleAddContact}
            disabled={isSaving || !email}
            aria-label="Add contact"
            title="Add contact"
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
              <path d="M15 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm-9-2V7H4v3H1v2h3v3h2v-3h3v-2H6zm9 4c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
            </svg>
          </button>
        )}
      </div>

      <div className="contact-hover-card-actions">
        <button
          type="button"
          className="contact-hover-primary-action"
          onClick={handleSendMail}
          disabled={!email}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
            <path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4-8 5-8-5V6l8 5 8-5v2z" />
          </svg>
          Send Mail
        </button>
      </div>
    </div>
  );
};

export default ContactHoverCard;
